import { useRef } from "react";
import { pickerValue } from "./postcard-dates";

/**
 * A date field that can be typed or picked.
 *
 * Joey asked for a picker "only if it's optional and I can still type", and both halves
 * of that are load-bearing. The text input is unchanged and is still the only thing named
 * in the form, so it is what gets submitted, it still takes a month like "2026-09" that no
 * date input can hold, and it can still be left blank. The picker writes into it.
 *
 * The picker is a button rather than a visible `<input type="date">` because that control
 * cannot be made to show only its calendar icon in every browser — Firefox has no
 * equivalent of ::-webkit-datetime-edit — and a second visible date box beside the text
 * one reads as two fields for one answer. So the date input is rendered but hidden, and
 * `showPicker()` opens it. Rendered, not `display: none`: showPicker throws on an element
 * that is not being rendered.
 *
 * If showPicker is missing or refuses, focus goes to the text input. That is the whole
 * fallback, and it is enough: typing was never routed through the picker.
 */
export function DateField({ label, pickLabel, name, value, onChange }: {
  label: string;
  pickLabel: string;
  name: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const picker = useRef<HTMLInputElement>(null);
  const typed = useRef<HTMLInputElement>(null);

  function openPicker() {
    try {
      picker.current?.showPicker();
    } catch {
      typed.current?.focus();
    }
  }

  return (
    <div className="date-field">
      <label>
        <span>{label}</span>
        <input
          ref={typed}
          name={name}
          inputMode="numeric"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder="YYYY-MM-DD or YYYY-MM"
        />
      </label>
      <button type="button" className="date-pick" onClick={openPicker} aria-label={pickLabel}>
        {/* Drawn rather than the calendar emoji, which renders full-colour and was the
            only thing on the form not in the green. currentColor keeps it in the palette
            and in step with the hover and focus states. */}
        <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor"
             strokeWidth="1.8" strokeLinecap="round" aria-hidden="true" focusable="false">
          <rect x="3.2" y="5" width="17.6" height="16" rx="2.4" />
          <path d="M3.2 9.8h17.6M8.2 3v4M15.8 3v4" />
        </svg>
      </button>
      {/* Hidden from the tab order and the accessibility tree: the button above is the
          control, and this is the machinery it opens. */}
      <input
        ref={picker}
        type="date"
        className="date-pick-native"
        tabIndex={-1}
        aria-hidden="true"
        value={pickerValue(value)}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
}
